/**
 * Year-on-year comparison maths for the Insights dashboards.
 *
 * ESG-3961: `getYoYChange` lived in two places — exported from
 * `pages/Reports/Insights/components/BigNumber.jsx` (used by four supplier components) and defined again,
 * byte-identical, as a private const in `SummaryCards.jsx`. It moved here because it needed a unit test and
 * Vitest cannot reach a private function in a `.jsx` component file (see `frontend/CLAUDE.md` — utils that
 * need tests live in their own standalone file).
 */

/**
 * The YoY % change, or null when there is no meaningful comparison — a missing figure on either side, or a
 * zero baseline. Null is the signal `YoYChip`/`BigNumber` use to render nothing at all, which is deliberate:
 * "0 %" would assert that the footprint held steady when in fact we don't know.
 *
 * Negative is an improvement (emissions fell) and renders green with a down arrow.
 */
export const getYoYChange = (current, previous) => {
  if (current == null || previous == null || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

/**
 * Read the same field off two payloads of the same shape, for a YoY comparison.
 *
 * ESG-3961: this exists to make a source mismatch **unwriteable** rather than merely discouraged. The Scope
 * 3 summary card compared `scope3Data.scopeThreeTotalCo2Emissions` (the persisted report,
 * `/reports/total-emissions`) against `previousYearData.scope3Total` (a live recompute, `/insights/total`),
 * so the percentage was a ratio between two different computations. Nothing about the JSX made that
 * visible — both props took a plausible-looking number.
 *
 * The two endpoints name the field differently — `scopeThreeTotalCo2Emissions` vs `scope3Total` — so a
 * single shared field name cannot span both. Routing a comparison through here means the mismatch stops
 * being expressible: you would have to pass a field name that only exists on one of the two payloads and
 * get a null.
 *
 * @param currentPayload  the current period's response
 * @param previousPayload the prior period's response, from the SAME endpoint
 * @param field           the field to read off both
 * @returns `{ current, previous }`, null where the payload or field is absent
 */
export const yoyPair = (currentPayload, previousPayload, field) => ({
  current: currentPayload?.[field] ?? null,
  previous: previousPayload?.[field] ?? null,
});
